import { defineClientModule } from '../client-module';

/**
 * Calls. Backed by the existing Worker route
 * GET /api/voice/calls, scoped to the actor's authorized clients and projected
 * through the canonical server-side client-safe Voice serializer.
 */
export const callsModule = defineClientModule({
  id: 'calls',
  recordTypeKey: 'call',
  label: 'Calls',
  route: '/ai-systems/calls',
  navGroup: 'AI Systems',
  icon: 'PhoneCall',
  description: 'Every call your AI agents have handled, with summaries and recordings.',
  source: { endpoint: '/api/voice/calls', tableKey: 'calls' },
  search: { placeholder: 'Caller, business, phone, reason, or summary' },
  filters: [
    {
      id: 'view',
      label: 'View',
      options: ['default', 'low_value', 'all', 'archived'],
    },
    {
      id: 'status',
      label: 'Status',
      options: ['All', 'queued', 'ringing', 'in-progress', 'ended', 'failed'],
    },
    {
      id: 'category',
      label: 'Call purpose',
      options: [
        'All', 'new_project_opportunity', 'existing_project_service_issue',
        'warranty_callback', 'billing', 'scheduling', 'vendor', 'employment',
        'solicitor', 'spam_robocall', 'wrong_number', 'other',
      ],
    },
    {
      id: 'lead_grade',
      label: 'Lead grade',
      options: ['All', 'a', 'b', 'c', 'd', 'unqualified'],
    },
  ],
  card: {
    title: 'caller_full_name',
    subtitle: 'business_name',
    badges: ['Status', 'call_category', 'lead_grade'],
    facts: [
      { source: 'follow_up_required', label: 'Follow-up', format: 'boolean' },
      { source: 'started_at', label: 'Started', format: 'datetime' },
      { source: 'duration_minutes', label: 'Duration', format: 'minutes' },
      { source: 'Agent, Type', label: 'Agent' },
      { source: 'reason_for_call', label: 'Reason' },
      { source: 'phone_number', label: 'Phone' },
      { source: 'email_address', label: 'Email' },
    ],
    summary: 'call_summary',
  },
  detail: {
    title: 'caller_full_name',
    subtitle: 'business_name',
    badges: ['Status', 'Type Of Call', 'urgency'],
    sections: [
      {
        id: 'caller',
        title: 'Caller',
        fields: [
          { source: 'caller_full_name', label: 'Name' },
          { source: 'business_name', label: 'Business' },
          { source: 'phone_number', label: 'Phone' },
          { source: 'email_address', label: 'Email' },
        ],
      },
      {
        id: 'call',
        title: 'Call',
        fields: [
          { source: 'Status', label: 'Status' },
          { source: 'Type Of Call', label: 'Type' },
          { source: 'urgency', label: 'Urgency' },
          { source: 'Agent, Type', label: 'Agent' },
          { source: 'reason_for_call', label: 'Reason' },
          { source: 'started_at', label: 'Started', format: 'datetime' },
          { source: 'ended_at', label: 'Ended', format: 'datetime' },
          { source: 'duration_minutes', label: 'Duration', format: 'minutes' },
          { source: 'caller_requested_recap', label: 'Requested Recap', format: 'boolean' },
        ],
      },
      {
        id: 'analysis',
        title: 'Analysis',
        fields: [
          { source: 'call_category', label: 'Category' },
          { source: 'lead_grade', label: 'Lead grade' },
          { source: 'grade_explanation', label: 'Why', long: true },
          { source: 'follow_up_required', label: 'Follow-up needed', format: 'boolean' },
          { source: 'recommended_next_action', label: 'Recommended next step' },
        ],
      },
      {
        id: 'summary',
        title: 'Summary',
        fields: [{ source: 'call_summary', label: 'Summary', long: true }],
      },
      {
        id: 'transcript',
        title: 'Transcript',
        fields: [{ source: 'call_trascript', label: 'Transcript', long: true }],
      },
    ],
    links: [{ label: 'Recording', source: 'call_recording_url' }],
  },
  clientSafeFields: [
    'Agent Avatar Lookup',
    'call_id',
    'caller_full_name',
    'business_name',
    'phone_number',
    'email_address',
    'Status',
    'Type Of Call',
    'started_at',
    'ended_at',
    'duration_minutes',
    'Agent, Type',
    'call_recording_url',
    'reason_for_call',
    'call_summary',
    'call_trascript',
    'urgency',
    'caller_requested_recap',
    'call_category',
    'lead_grade',
    'grade_explanation',
    'follow_up_required',
    'recommended_next_action',
  ],
  emptyState: {
    title: 'No calls to show.',
    body: 'Try clearing your search and filters. New calls appear here shortly after they finish.',
  },
  actions: [{ id: 'refresh', label: 'Refresh' }],
});
